/**
 * KalendarPage — month + week workspace.
 *
 * Layout: Shell · AppRail (kalendar active) · Crown (breadcrumb + Měsíc/Týden
 * + CrownMeta + view subtabs) · prev/next/today band · grid body · Inspector
 * previewing the selected day's items.
 *
 * Every item is single-day and owned by another module — the calendar only
 * lays them out and deep-links to their detail:
 *   - vehicle handovers (`/v1/kalendar/predani`) → `/vozidla/smlouvy/$id`
 *   - open tasks → `/ukoly/$id`
 *   - planned contacts → `/kontakty/$id`
 *   - used-car STK / servisní prohlídka deadlines → `/ojeta-vozidla/$id`
 * Tasks, plans and deadlines are overlays (`useKalendarOverlays`), each
 * toggleable from the "Zobrazit" menu and gated on the module's read grant.
 * Calendar events of its own (kalendar_udalost) were retired in 0035.
 *
 * **Month view** — `Calendar.MonthHeader` + `Calendar.MonthGrid`, a
 * `<CalendarCell>` per day; caps at 3 chips per cell + "+N další" overflow.
 *
 * **Week view** — same `<CalendarCell>` primitive in a single 7-col row
 * anchored on `den`'s Monday-based week (a day-grouped agenda on phones).
 * Weeks that straddle months fetch both months' handovers, deduped by id.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';
import { Fragment, useMemo, useState } from 'react';
import { AgendaView } from '@/components/AgendaView';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Calendar } from '@/components/Calendar';
import { CalendarCell } from '@/components/CalendarCell';
import { Chip } from '@/components/Chip';
import { Menu } from '@/components/Menu';
import { Crown } from '@/components/Crown';
import { CrownMeta } from '@/components/CrownMeta';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { EventChip } from '@/components/EventChip';
import { Icon } from '@/components/Icon';
import { Inspector } from '@/components/Inspector';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { Subtabs } from '@/components/Subtabs';
import { formatInt } from '@/lib/format';
import { useModuleAccess } from '@/lib/permissions';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { useKalendarPredaniQuery, type CalendarDelivery } from './api';
import { useMeQuery } from '@/modules/dashboard/api';
import { usePlannedContactsQuery } from '@/modules/kontakty/api';
import { useOjetaTerminyQuery } from '@/modules/ojeta-vozidla/api';
import { mergeTerminy } from '@/modules/ojeta-vozidla/helpers';
import { useVozidlaTerminyQuery } from '@/modules/vozidla/api';
import { useTasksQuery } from '@/modules/ukoly/api';
import { useKalendarPrefs, type KalendarPrefs } from './prefs';

import {
  CESKY_MESIC,
  DNY_ZAHLAVI_FULL,
  DNY_ZAHLAVI_SHORT,
  bucketByDay,
  daysInMonth,
  firstWeekdayMondayBased,
  formatDayLong,
  formatDayShort,
  itemTone,
  itemsForIsoDay,
  shiftIsoDays,
  todayIso,
  weekDaysFor,
  type KalendarItem,
  type KalendarOverlays,
  type KalendarSearch,
  type KalendarView,
} from './helpers';

const VIEW_TABS: { value: KalendarView; label: string }[] = [
  { value: 'mesic', label: 'Měsíc' },
  { value: 'tyden', label: 'Týden' },
];

export function KalendarPage() {
  const view = (useSearch({ from: '/kalendar' }) as KalendarSearch).view ?? 'mesic';
  return view === 'tyden' ? <WeekView /> : <MonthView />;
}

/* ────────────────────────────────────────────────────────────────────────
   Shared chrome
   ──────────────────────────────────────────────────────────────────────── */

interface ChromeProps {
  view: KalendarView;
  /** Inline page title (CrownMeta sits beside it). */
  heading: React.ReactNode;
  meta: { label: string; value: string }[];
  band: React.ReactNode;
  inspector: React.ReactNode;
  children: React.ReactNode;
}

function KalendarChrome({ view, heading, meta, band, inspector, children }: ChromeProps) {
  const navigate = useNavigate();
  const urlSearch = useSearch({ from: '/kalendar' }) as KalendarSearch;

  const setView = (nextView: KalendarView) => {
    if (nextView === view) return;
    if (nextView === 'tyden') {
      navigate({
        to: '/kalendar',
        search: {
          ...urlSearch,
          view: 'tyden',
          den: urlSearch.den ?? todayIso(),
          mesic: undefined,
          rok: undefined,
        },
      });
    } else {
      navigate({
        to: '/kalendar',
        search: {
          ...urlSearch,
          view: undefined,
          den: undefined,
        },
      });
    }
  };

  return (
    <Shell rail={<AppRail active="kalendar" />} inspector={inspector}>
      <Crown>
        <Breadcrumb segments={[{ label: 'Kalendář' }, { label: 'Přehled' }]} />
        <Crown.Row className="mt-3">
          {heading}
          <CrownMeta items={meta} />
        </Crown.Row>
        <Crown.Row className="mt-3">
          <Subtabs<KalendarView>
            aria-label="Pohled"
            items={VIEW_TABS.map((t) => ({ value: t.value, label: t.label }))}
            value={view}
            onChange={setView}
          />
        </Crown.Row>
      </Crown>
      {band}
      {children}
    </Shell>
  );
}

interface NavBandProps {
  prevLabel: string;
  nextLabel: string;
  onPrev: () => void;
  onNext: () => void;
  /** Primary scale label ("Měsíc" / "Týden"). */
  scale: string;
  /** Headline (month name or week number). */
  headline: React.ReactNode;
  /** Trailing detail (year or date range). */
  trail?: React.ReactNode;
  /** Optional "Zpět na dnešní …" button. */
  resetTodayLabel?: string;
  onResetToday?: () => void;
  /** Right-aligned filter controls (Moje · Zobrazit). */
  filters?: React.ReactNode;
}

function NavBand({
  prevLabel,
  nextLabel,
  onPrev,
  onNext,
  scale,
  headline,
  trail,
  resetTodayLabel,
  onResetToday,
  filters,
}: NavBandProps) {
  return (
    <div
      data-testid="kalendar-band"
      className="flex items-center gap-3.5 border-b border-line bg-paper px-8 py-3.5 max-lg:flex-wrap max-lg:gap-x-3 max-lg:gap-y-2 max-sm:px-4 max-sm:py-2.5"
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={prevLabel}
          onClick={onPrev}
          className="grid size-7 place-items-center border border-line bg-paper text-ink-2 hover:border-ink hover:text-ink max-sm:size-10"
        >
          <Icon name="chevronL" size={13} />
        </button>
        <button
          type="button"
          aria-label={nextLabel}
          onClick={onNext}
          className="grid size-7 place-items-center border border-line bg-paper text-ink-2 hover:border-ink hover:text-ink max-sm:size-10"
        >
          <Icon name="chevronR" size={13} />
        </button>
      </div>
      <div className="flex items-baseline gap-2.5">
        <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
          {scale}
        </span>
        <span className="text-[26px] font-bold leading-none tracking-[-0.02em] text-ink max-sm:text-[20px]">
          {headline}
        </span>
        {trail && <span className="text-[13px] text-ink-2">{trail}</span>}
      </div>
      {resetTodayLabel && onResetToday && (
        <button
          type="button"
          onClick={onResetToday}
          className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-accent-text hover:underline"
        >
          {resetTodayLabel}
        </button>
      )}
      <div className="flex-1" />
      {filters}
    </div>
  );
}

/**
 * `terminy` returns every overdue deadline plus those due within `dny` days;
 * 365 is the API maximum, so deadlines more than a year out don't show.
 */
const TERMIN_WINDOW_DAYS = 365;

interface OverlayState {
  overlays: KalendarOverlays;
  /** Which overlays are actually rendered (pref on + module read grant). */
  shown: { tasks: boolean; plans: boolean; terminy: boolean };
  /** Which overlays the member may toggle at all. */
  available: { plans: boolean; terminy: boolean };
}

/** Tasks, planned contacts and used-car deadlines layered over the month data. */
function useKalendarOverlays(prefs: KalendarPrefs): OverlayState {
  const { can } = useModuleAccess();
  const me = useMeQuery();
  const myId = me.data?.user.id ?? null;

  const available = {
    plans: can('kontakty', 'read'),
    terminy: can('ojeta_vozidla', 'read') || can('vozidla', 'read'),
  };
  const shown = {
    tasks: prefs.showTasks,
    plans: prefs.showPlans && available.plans,
    terminy: prefs.showTerminy && available.terminy,
  };

  const tasksQuery = useTasksQuery(
    prefs.showMine ? { mine: true, stav: 'otevrene' } : { stav: 'otevrene' },
    { enabled: shown.tasks },
  );
  // "Moje" scopes plans by seller. Hold the fetch while /me is in flight so we
  // don't fire an unscoped request we'd discard; if /me fails, fall back to
  // the whole company (same as the Plánované workspace).
  const plansQuery = usePlannedContactsQuery(
    prefs.showMine && myId ? { seller: myId } : {},
    { enabled: shown.plans && !(prefs.showMine && me.isLoading) },
  );
  // Deadlines of the used cars and of the registered new cars, each behind its own grant.
  const terminyQuery = useOjetaTerminyQuery(TERMIN_WINDOW_DAYS, {
    enabled: shown.terminy && can('ojeta_vozidla', 'read'),
    rozsah: 'skupina',
  });
  const noveTerminyQuery = useVozidlaTerminyQuery(TERMIN_WINDOW_DAYS, {
    enabled: shown.terminy && can('vozidla', 'read'),
    rozsah: 'skupina',
  });

  const tasks = shown.tasks ? tasksQuery.data?.tasks : undefined;
  const plans = shown.plans ? plansQuery.data?.contacts : undefined;
  const terminy = shown.terminy
    ? mergeTerminy(terminyQuery.data?.terminy, noveTerminyQuery.data?.terminy)
    : undefined;
  const overlays = useMemo<KalendarOverlays>(
    () => ({ tasks, plans, terminy }),
    [tasks, plans, terminy],
  );

  return { overlays, shown, available };
}

/** Meta counts for the overlays that are switched on. */
function overlayMeta(
  shown: OverlayState['shown'],
  count: (kind: KalendarItem['kind']) => number,
): { label: string; value: string }[] {
  return [
    ...(shown.tasks ? [{ label: 'Úkolů', value: formatInt(count('task')) }] : []),
    ...(shown.plans ? [{ label: 'Plánů', value: formatInt(count('plan')) }] : []),
    ...(shown.terminy ? [{ label: 'Termínů', value: formatInt(count('termin')) }] : []),
  ];
}

type LayerKey = 'showTasks' | 'showPlans' | 'showTerminy';

/**
 * Moje (scope tasks + plans to me) + a "Zobrazit" menu toggling each overlay —
 * persisted via localStorage.
 */
function KalendarFilters({
  prefs,
  available,
  onChange,
}: {
  prefs: KalendarPrefs;
  available: OverlayState['available'];
  onChange: (patch: Partial<KalendarPrefs>) => void;
}) {
  const layers: { key: LayerKey; label: string }[] = [
    { key: 'showTasks', label: 'Úkoly' },
    ...(available.plans ? [{ key: 'showPlans' as const, label: 'Plánované kontakty' }] : []),
    ...(available.terminy
      ? [{ key: 'showTerminy' as const, label: 'Termíny STK a servisu' }]
      : []),
  ];
  const visible = layers.filter((l) => prefs[l.key]).length;
  const personal = prefs.showTasks || (available.plans && prefs.showPlans);
  return (
    <div className="flex items-center gap-2.5">
      <Chip
        active={prefs.showMine}
        disabled={!personal}
        onClick={() => onChange({ showMine: !prefs.showMine })}
        data-testid="kalendar-moje"
      >
        Moje
      </Chip>
      <Menu>
        <Menu.Trigger asChild>
          <Chip
            icon={<Icon name="filter" size={12} />}
            active={visible < layers.length}
            data-testid="kalendar-zobrazit"
          >
            {visible < layers.length ? `Zobrazit · ${visible}/${layers.length}` : 'Zobrazit'}
          </Chip>
        </Menu.Trigger>
        <Menu.Content>
          <Menu.Label>Zobrazit v kalendáři</Menu.Label>
          {layers.map((l) => (
            <Menu.CheckItem
              key={l.key}
              checked={prefs[l.key]}
              onCheckedChange={(checked) => onChange({ [l.key]: checked })}
              // Keep the menu open so several layers can be toggled at once.
              onSelect={(e) => e.preventDefault()}
            >
              {l.label}
            </Menu.CheckItem>
          ))}
        </Menu.Content>
      </Menu>
    </div>
  );
}

function useHandleItemClick(): (item: KalendarItem) => void {
  const navigate = useNavigate();
  return (item) => {
    if (item.kind === 'delivery') {
      navigate({
        to: '/vozidla/smlouvy/$id',
        params: { id: String(item.id) },
      });
    } else if (item.kind === 'task') {
      // No list cohort from the calendar — show the whole company on the detail
      // rail so the opened task is always present (not an empty "Moje" rail).
      navigate({ to: '/ukoly/$id', params: { id: String(item.id) }, search: { mine: false, stav: 'otevrene' } });
    } else if (item.kind === 'plan') {
      navigate({ to: '/kontakty/$id', params: { id: String(item.id) } });
    } else {
      navigate(
        item.zdroj === 'nove'
          ? { to: '/vozidla/$id', params: { id: String(item.id) } }
          : { to: '/ojeta-vozidla/$id', params: { id: String(item.id) } },
      );
    }
  };
}

/* ────────────────────────────────────────────────────────────────────────
   Month view (9.1)
   ──────────────────────────────────────────────────────────────────────── */

function MonthView() {
  const navigate = useNavigate();
  const urlSearch = useSearch({ from: '/kalendar' }) as KalendarSearch;
  const handleItemClick = useHandleItemClick();

  const today = todayIso();
  const todayParts = today.split('-').map((x) => parseInt(x, 10));
  const mesic = urlSearch.mesic ?? todayParts[1] ?? 1;
  const rok = urlSearch.rok ?? todayParts[0] ?? new Date().getFullYear();

  const monthQuery = useKalendarPredaniQuery(mesic, rok);
  const deliveries = useMemo(() => monthQuery.data?.deliveries ?? [], [monthQuery.data]);

  const [prefs, setPrefs] = useKalendarPrefs();
  const { overlays, shown, available } = useKalendarOverlays(prefs);

  const buckets = useMemo(
    () => bucketByDay(rok, mesic, deliveries, overlays),
    [rok, mesic, deliveries, overlays],
  );

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const todayInMonth = todayParts[0] === rok && todayParts[1] === mesic;
  const initialSelected = useMemo(() => {
    if (selectedDay) return selectedDay;
    if (todayInMonth) return today;
    return `${rok}-${String(mesic).padStart(2, '0')}-01`;
  }, [selectedDay, todayInMonth, today, rok, mesic]);

  const selectedItems = useMemo(
    () => itemsForIsoDay(initialSelected, deliveries, overlays),
    [initialSelected, deliveries, overlays],
  );

  const setMonth = (nextMesic: number, nextRok: number) => {
    let mm = nextMesic;
    let yy = nextRok;
    if (mm < 1) {
      mm = 12;
      yy -= 1;
    } else if (mm > 12) {
      mm = 1;
      yy += 1;
    }
    navigate({
      to: '/kalendar',
      search: { ...urlSearch, mesic: mm, rok: yy },
      replace: true,
    });
    setSelectedDay(null);
  };

  const dim = daysInMonth(rok, mesic);
  const leading = firstWeekdayMondayBased(rok, mesic);
  const cellCount = leading + dim;
  const trailing = (7 - (cellCount % 7)) % 7;

  // Overlays are single-day, so counting bucket chips counts each item once.
  const countKind = (kind: KalendarItem['kind']) => {
    let n = 0;
    for (const items of buckets.values()) n += items.filter((i) => i.kind === kind).length;
    return n;
  };
  const totals = {
    deliveries: deliveries.length,
    busyDays: buckets.size,
  };

  return (
    <KalendarChrome
      view="mesic"
      heading={
        <DisplayHeading>
          {CESKY_MESIC[mesic]} <em>{rok}</em>
        </DisplayHeading>
      }
      meta={[
        { label: 'Předání', value: formatInt(totals.deliveries) },
        ...overlayMeta(shown, countKind),
        { label: 'Obsazené dny', value: formatInt(totals.busyDays) },
      ]}
      band={
        <NavBand
          prevLabel="Předchozí měsíc"
          nextLabel="Následující měsíc"
          onPrev={() => setMonth(mesic - 1, rok)}
          onNext={() => setMonth(mesic + 1, rok)}
          scale="Měsíc"
          headline={CESKY_MESIC[mesic]}
          trail={String(rok)}
          resetTodayLabel={!todayInMonth ? 'Zpět na dnešní měsíc' : undefined}
          onResetToday={
            !todayInMonth
              ? () => setMonth(todayParts[1] ?? 1, todayParts[0] ?? rok)
              : undefined
          }
          filters={<KalendarFilters prefs={prefs} available={available} onChange={setPrefs} />}
        />
      }
      inspector={
        <DayInspector
          iso={initialSelected}
          items={selectedItems}
          isLoading={monthQuery.isLoading}
        />
      }
    >
      <div
        data-testid="kalendar-page"
        className="flex min-h-0 flex-1 flex-col px-8 pb-6 pt-3.5 max-sm:overflow-y-auto max-sm:px-4 max-sm:pb-4"
      >
        {monthQuery.isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : monthQuery.isError ? (
          <EmptyState
            eyebrow="Chyba"
            title="Kalendář se nepodařilo načíst"
            description="Zkuste obnovit stránku. Pokud problém trvá, kontaktujte správce."
          />
        ) : (
          <Calendar scrollY>
            <Calendar.MonthHeader weekdays={DNY_ZAHLAVI_FULL} />
            <Calendar.MonthGrid>
              {Array.from({ length: leading }).map((_, i) => (
                <CalendarCell key={`lead-${i}`} date="" muted />
              ))}
              {Array.from({ length: dim }).map((_, i) => {
                const day = i + 1;
                const iso = `${rok}-${String(mesic).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const items = buckets.get(day) ?? [];
                const isToday = iso === today;
                const isSelected = iso === initialSelected;
                return (
                  <CalendarCell
                    key={iso}
                    date={String(day)}
                    today={isToday}
                    marker={isToday ? '— Dnes' : undefined}
                    onClick={() => setSelectedDay(iso)}
                    className={
                      isSelected && !isToday ? 'ring-1 ring-inset ring-ink' : ''
                    }
                    minHeight={108}
                  >
                    {items.slice(0, 3).map((item) => (
                      <EventChip
                        key={item.key}
                        tone={itemTone(item)}
                        title={item.title}
                        sub={item.sub}
                        onClick={() => handleItemClick(item)}
                      />
                    ))}
                    {items.length > 3 && (
                      <span className="mt-px font-mono text-[10px] tracking-[-0.02em] text-ink-3">
                        + {items.length - 3} další
                      </span>
                    )}
                  </CalendarCell>
                );
              })}
              {Array.from({ length: trailing }).map((_, i) => (
                <CalendarCell key={`trail-${i}`} date="" muted />
              ))}
            </Calendar.MonthGrid>
          </Calendar>
        )}
      </div>
    </KalendarChrome>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   Week view (9.2)
   ──────────────────────────────────────────────────────────────────────── */

/** ISO-8601 week number (Mon-anchored) for a given ISO day. */
function isoWeekNumber(iso: string): number {
  const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

function WeekView() {
  const navigate = useNavigate();
  const urlSearch = useSearch({ from: '/kalendar' }) as KalendarSearch;
  const handleItemClick = useHandleItemClick();
  // Phone swaps the 7-column week strip (unreadable at 390px) for a
  // day-grouped agenda list; tablet + desktop keep the strip.
  const { isPhone } = useBreakpoint();

  const today = todayIso();
  const anchor = urlSearch.den ?? today;
  const days = useMemo(() => weekDaysFor(anchor), [anchor]);
  const monday = days[0]!;
  const sunday = days[6]!;

  // Determine month query (single, or two when straddling months).
  const [yA, mA] = monday.split('-').map((x) => parseInt(x, 10));
  const [yB, mB] = sunday.split('-').map((x) => parseInt(x, 10));
  const monthA = useKalendarPredaniQuery(mA ?? 1, yA ?? new Date().getFullYear());
  const needsSecond = mA !== mB || yA !== yB;
  // Always call (hook order), but the second hook returns no data when not
  // needed because we query the same month — minor over-fetch dodged via
  // staleTime in queryClient.
  const monthB = useKalendarPredaniQuery(
    needsSecond ? (mB ?? 1) : (mA ?? 1),
    needsSecond ? (yB ?? new Date().getFullYear()) : (yA ?? new Date().getFullYear()),
  );

  const deliveries = useMemo(() => {
    const delMap = new Map<number, CalendarDelivery>();
    for (const r of [monthA.data, needsSecond ? monthB.data : undefined]) {
      if (!r) continue;
      for (const d of r.deliveries) delMap.set(d.id_smlouvy, d);
    }
    return [...delMap.values()];
  }, [monthA.data, monthB.data, needsSecond]);

  const [prefs, setPrefs] = useKalendarPrefs();
  const { overlays, shown, available } = useKalendarOverlays(prefs);

  const isLoading = monthA.isLoading || (needsSecond && monthB.isLoading);
  const isError = monthA.isError || (needsSecond && monthB.isError);

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const initialSelected = useMemo(() => {
    if (selectedDay && days.includes(selectedDay)) return selectedDay;
    if (days.includes(today)) return today;
    return monday;
  }, [selectedDay, days, today, monday]);

  const selectedItems = useMemo(
    () => itemsForIsoDay(initialSelected, deliveries, overlays),
    [initialSelected, deliveries, overlays],
  );

  const setAnchor = (next: string) => {
    navigate({
      to: '/kalendar',
      search: { ...urlSearch, view: 'tyden', den: next },
      replace: true,
    });
    setSelectedDay(null);
  };

  const weekItems = useMemo(
    () => days.flatMap((iso) => itemsForIsoDay(iso, deliveries, overlays)),
    [days, deliveries, overlays],
  );
  const countKind = (kind: KalendarItem['kind']) =>
    weekItems.filter((i) => i.kind === kind).length;
  const busyDays = useMemo(
    () => days.filter((iso) => itemsForIsoDay(iso, deliveries, overlays).length > 0).length,
    [days, deliveries, overlays],
  );

  const weekNum = isoWeekNumber(monday);
  const todayInWeek = days.includes(today);
  const rangeLabel = `${formatDayShort(monday)} – ${formatDayLong(sunday)}`;

  return (
    <KalendarChrome
      view="tyden"
      heading={
        <DisplayHeading>
          Týden {weekNum}, <em>{monday.slice(0, 4)}</em>
        </DisplayHeading>
      }
      meta={[
        { label: 'Předání', value: formatInt(countKind('delivery')) },
        ...overlayMeta(shown, countKind),
        { label: 'Obsazené dny', value: formatInt(busyDays) },
      ]}
      band={
        <NavBand
          prevLabel="Předchozí týden"
          nextLabel="Následující týden"
          onPrev={() => setAnchor(shiftIsoDays(monday, -7))}
          onNext={() => setAnchor(shiftIsoDays(monday, 7))}
          scale="Týden"
          headline={String(weekNum)}
          trail={rangeLabel}
          resetTodayLabel={!todayInWeek ? 'Zpět na dnešní týden' : undefined}
          onResetToday={!todayInWeek ? () => setAnchor(today) : undefined}
          filters={<KalendarFilters prefs={prefs} available={available} onChange={setPrefs} />}
        />
      }
      inspector={
        <DayInspector iso={initialSelected} items={selectedItems} isLoading={isLoading} />
      }
    >
      <div
        data-testid="kalendar-page"
        className={
          isPhone
            ? 'min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-1.5'
            : 'flex min-h-0 flex-1 flex-col px-8 pb-6 pt-3.5'
        }
      >
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : isError ? (
          <EmptyState
            eyebrow="Chyba"
            title="Kalendář se nepodařilo načíst"
            description="Zkuste obnovit stránku. Pokud problém trvá, kontaktujte správce."
          />
        ) : isPhone ? (
          <AgendaView>
            {days.map((iso, i) => {
              const dayItems = itemsForIsoDay(iso, deliveries, overlays);
              return (
                <Fragment key={iso}>
                  <AgendaView.Hour>
                    {DNY_ZAHLAVI_SHORT[i]} {formatDayShort(iso)}
                    {iso === today ? ' — Dnes' : ''}
                  </AgendaView.Hour>
                  {dayItems.map((item) => (
                    <AgendaView.Job
                      key={item.key}
                      title={item.title}
                      task={item.sub}
                      tone={itemTone(item)}
                      onClick={() => handleItemClick(item)}
                    />
                  ))}
                  {dayItems.length === 0 && (
                    <p className="pb-1 font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                      — Volno
                    </p>
                  )}
                </Fragment>
              );
            })}
          </AgendaView>
        ) : (
          <Calendar>
            <Calendar.MonthHeader weekdays={DNY_ZAHLAVI_FULL} />
            <div className="grid grid-cols-7 [&>*:nth-child(7n)]:border-r-0">
              {days.map((iso) => {
                const dayItems = itemsForIsoDay(iso, deliveries, overlays);
                const isToday = iso === today;
                const isSelected = iso === initialSelected;
                const dayNum = parseInt(iso.slice(8, 10), 10);
                const monthNum = parseInt(iso.slice(5, 7), 10);
                return (
                  <CalendarCell
                    key={iso}
                    label={DNY_ZAHLAVI_SHORT[days.indexOf(iso)]}
                    date={`${dayNum}. ${monthNum}.`}
                    today={isToday}
                    marker={isToday ? '— Dnes' : undefined}
                    onClick={() => setSelectedDay(iso)}
                    className={
                      isSelected && !isToday ? 'ring-1 ring-inset ring-ink' : ''
                    }
                    minHeight={420}
                  >
                    {dayItems.map((item) => (
                      <EventChip
                        key={item.key}
                        tone={itemTone(item)}
                        title={item.title}
                        sub={item.sub}
                        onClick={() => handleItemClick(item)}
                      />
                    ))}
                    {dayItems.length === 0 && (
                      <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
                        — Volno
                      </span>
                    )}
                  </CalendarCell>
                );
              })}
            </div>
          </Calendar>
        )}
      </div>
    </KalendarChrome>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   Day inspector (shared)
   ──────────────────────────────────────────────────────────────────────── */

interface DayInspectorProps {
  iso: string;
  items: KalendarItem[];
  isLoading: boolean;
}

function DayInspector({ iso, items, isLoading }: DayInspectorProps) {
  const handleItemClick = useHandleItemClick();
  const dateLabel = formatDayLong(iso);
  return (
    <Inspector>
      <Inspector.Head
        eyebrow={<>— Vybraný den</>}
        title={
          <span>
            {dateLabel.split(' ').slice(0, 2).join(' ')}
            <br />
            <em className="font-medium italic text-ink-2">
              {dateLabel.split(' ').slice(2).join(' ')}.
            </em>
          </span>
        }
        sub={<span className="font-mono text-[11.5px] text-ink-3">{iso}</span>}
      />
      <Inspector.Body className="flex flex-col gap-5 px-6 py-5">
        {isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : items.length === 0 ? (
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-3">
            Nic naplánováno
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((item) => (
              <li key={item.key}>
                <EventChip
                  tone={itemTone(item)}
                  title={item.title}
                  sub={item.sub}
                  onClick={() => handleItemClick(item)}
                />
              </li>
            ))}
          </ul>
        )}
      </Inspector.Body>
    </Inspector>
  );
}
